import React, {useMemo} from 'react'
import {Accordion} from 'react-daisyui'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'
import clsx from 'clsx'
import {useScrollGradients} from '../../hooks/useScrollGradients'

/**
 * Payment schedule item from the payment-installments API
 */
interface PaymentScheduleItem {
  folioId: string
  chargeDate: string
  folioBalance: {
    amount: number
    currency: string
  }
  billingPeriod: {
    startDate: string
    endDate: string
    formattedPeriod: string
    installmentLabel: string
  }
  routing: {
    id: string
    filter: {
      from: string
      to: string
    }
  }
}

/**
 * Payment history item from the payment-installments API
 */
interface PaymentHistoryItem {
  folioId: string
  mainFolio: boolean
  payments: {
    id: string
    amount: {
      amount: number
      currency: string
    }
    timestamp?: string
    created?: string
    method?: string
    status?: string
  }[]
  billingPeriod: {
    startDate: string
    endDate: string
    formattedPeriod: string
    installmentLabel: string
  }
}

interface Folio {
  id: string
  balance?: {
    amount: number
    currency: string
  }
}

interface PaymentInstallmentScheduleSectionProps {
  isOpen: boolean
  onToggle: () => void
  isLoading: boolean
  schedule: PaymentScheduleItem[]
  paymentHistory?: PaymentHistoryItem[]
  checkinDate?: string
  checkoutDate?: string
  folios?: Folio[]
  foliosLoading?: boolean
}

/**
 * Format payment info from payment schedule item
 * Uses the actual chargeDate from the payment-installments API scheduler
 *
 * Note: This section only displays FUTURE scheduled payments.
 * The first installment is paid at booking time and is not included here.
 */
function formatPaymentInfo(
  item: PaymentScheduleItem,
  index: number,
  totalItems: number
) {
  if (!item.billingPeriod) {
    return null
  }

  const {startDate, endDate, formattedPeriod} = item.billingPeriod
  const isLastInstallment = index === totalItems - 1

  // Use the actual charge date from the scheduler
  const chargeDate = new Date(item.chargeDate)

  // Calculate days in the billing period
  const start = new Date(startDate)
  const end = new Date(endDate)
  const daysDiff =
    Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1

  const formatOptions: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }

  const dueDateLabel = `Due ${chargeDate.toLocaleDateString(
    'en-US',
    formatOptions
  )}`
  const description = `Pay for the ${isLastInstallment ? 'last' : 'next'} ${
    daysDiff === 1 ? 'day' : `${daysDiff} days`
  } of your reservation.`

  return {
    dueDateLabel,
    dueDate: chargeDate,
    billingPeriod: formattedPeriod,
    description
  }
}

/**
 * Format payment history info from payment history item
 * Shows the date paid and billing period
 * @param item - The payment history item
 * @param fallbackChargeDate - Optional charge date from schedule to use if no payment date
 */
function formatPaymentHistoryInfo(
  item: PaymentHistoryItem,
  fallbackChargeDate?: string
) {
  // Get the payment date from the first successful payment
  const successfulPayment =
    item.payments?.find(
      p => p.status === 'Success' || p.status === 'Pending' || !p.status
    ) || item.payments?.[0]

  const paymentDate = successfulPayment?.timestamp || successfulPayment?.created

  const formatOptions: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }

  let paidDateLabel: string
  if (paymentDate) {
    // Use actual payment date
    paidDateLabel = new Date(paymentDate).toLocaleDateString(
      'en-US',
      formatOptions
    )
  } else if (fallbackChargeDate) {
    // Fall back to due date from schedule
    paidDateLabel = new Date(fallbackChargeDate).toLocaleDateString(
      'en-US',
      formatOptions
    )
  } else {
    // Last resort fallback
    paidDateLabel = 'Paid'
  }

  // Use formattedPeriod if available, otherwise format from dates
  let billingPeriod = item.billingPeriod?.formattedPeriod || ''
  if (
    !billingPeriod &&
    item.billingPeriod?.startDate &&
    item.billingPeriod?.endDate
  ) {
    const periodFormatOptions: Intl.DateTimeFormatOptions = {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }
    const start = new Date(item.billingPeriod.startDate)
    const end = new Date(item.billingPeriod.endDate)
    billingPeriod = `${start.toLocaleDateString(
      'en-US',
      periodFormatOptions
    )} - ${end.toLocaleDateString('en-US', periodFormatOptions)}`
  }

  return {
    paidDateLabel,
    billingPeriod,
    paymentStatus: successfulPayment?.status || 'Success'
  }
}

/**
 * Calculate total amount from payments in a history item
 */
function calculatePaymentTotal(
  payments: PaymentHistoryItem['payments']
): number {
  if (!Array.isArray(payments)) {
    return 0
  }
  return payments.reduce((sum, payment) => {
    // Handle different amount structures from Apaleo
    // Primary: payment.amount.amount (nested object)
    // Fallback: payment.amount if it's a number directly
    let numAmount = 0
    if (payment?.amount?.amount !== undefined) {
      numAmount =
        typeof payment.amount.amount === 'number'
          ? payment.amount.amount
          : parseFloat(String(payment.amount.amount)) || 0
    } else if (typeof payment?.amount === 'number') {
      numAmount = payment.amount
    }
    return sum + Math.abs(numAmount)
  }, 0)
}

const PaymentInstallmentScheduleSection: React.FC<PaymentInstallmentScheduleSectionProps> = ({
  isOpen,
  onToggle,
  isLoading,
  schedule,
  paymentHistory = [],
  checkinDate,
  checkoutDate,
  folios = [],
  foliosLoading = false
}) => {
  // Create a lookup map from folioId to chargeDate from the full schedule
  const chargeDateByFolioId = useMemo(() => {
    const map: Record<string, string> = {}
    schedule.forEach(item => {
      if (item.folioId && item.chargeDate) {
        map[item.folioId] = item.chargeDate
      }
    })
    return map
  }, [schedule])

  // Sort payment history by billing period start date (oldest first)
  const sortedPaymentHistory = useMemo(() => {
    return [...paymentHistory].sort((a, b) => {
      const aStartDate = a.billingPeriod?.startDate
        ? new Date(a.billingPeriod.startDate).getTime()
        : 0
      const bStartDate = b.billingPeriod?.startDate
        ? new Date(b.billingPeriod.startDate).getTime()
        : 0
      return aStartDate - bStartDate
    })
  }, [paymentHistory])

  const {
    showTopGradient,
    showBottomGradient,
    scrollContainerRef
  } = useScrollGradients([schedule])

  const {
    showTopGradient: showHistoryTopGradient,
    showBottomGradient: showHistoryBottomGradient,
    scrollContainerRef: historyScrollContainerRef
  } = useScrollGradients([sortedPaymentHistory])

  return (
    <section
      className={`collapse collapse-arrow ${isOpen ? 'collapse-open' : ''}`}
      aria-label="Payment Installment Schedule Section"
    >
      <input
        type="checkbox"
        name="res-accordion"
        aria-controls="payment-schedule-accordion"
        checked={isOpen}
        onChange={onToggle}
      />
      <Accordion.Title className="accordion-title">
        Payment Installment Schedule
      </Accordion.Title>
      <Accordion.Content className="accordion-content">
        <div className="w-full h-px bg-gray-200 mb-6" />

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <LoadingSpinner size={LoadingSpinnerSize.Medium} />
            <p className="text-sm text-gray-600 mt-4 italic">
              Generating Payment Installment Schedule...
            </p>
          </div>
        ) : (
          <div className="flex flex-col w-full gap-8">
            <style>{`
              .hide-scrollbar::-webkit-scrollbar {
                display: none;
              }
            `}</style>

            {/* Upcoming Payments Section */}
            {schedule.length > 0 && (
              <div className="flex flex-col">
                <h3 className="text-lg font-bold mb-4">Upcoming Payments</h3>
                <div className="relative">
                  {showTopGradient && schedule.length > 3 && (
                    <div
                      className="absolute top-0 left-0 right-0 h-10
                        bg-gradient-to-t from-white/0 to-white
                        pointer-events-none z-[1]"
                    />
                  )}
                  <div
                    ref={scrollContainerRef}
                    className="flex flex-col gap-4 hide-scrollbar pr-2 pb-2"
                    style={{
                      maxHeight: schedule.length > 3 ? '400px' : 'auto',
                      overflowY: schedule.length > 3 ? 'auto' : 'visible',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none'
                    }}
                  >
                    {(() => {
                      const today = new Date('2026-02-20')
                      today.setHours(0, 0, 0, 0)

                      // Filter out items with $0 amount
                      const nonZeroSchedule = schedule.filter(
                        (item: PaymentScheduleItem) =>
                          Math.abs(item.folioBalance?.amount || 0) > 0
                      )

                      // Find the next upcoming payment index in the filtered array
                      let nextPaymentIndex = -1
                      for (let i = 0; i < nonZeroSchedule.length; i++) {
                        const paymentInfo = formatPaymentInfo(
                          nonZeroSchedule[i],
                          i,
                          nonZeroSchedule.length
                        )
                        if (paymentInfo && paymentInfo.dueDate >= today) {
                          nextPaymentIndex = i
                          break
                        }
                      }

                      return nonZeroSchedule.map(
                        (item: PaymentScheduleItem, index: number) => {
                          const totalItems = nonZeroSchedule.length
                          const paymentInfo = formatPaymentInfo(
                            item,
                            index,
                            totalItems
                          )
                          if (!paymentInfo) {
                            return null
                          }

                          // Use folioBalance from payment schedule (from payment-installments API)
                          const amountPerInstallment = Math.abs(
                            item.folioBalance?.amount || 0
                          )
                          const isNextPayment = index === nextPaymentIndex

                          // Check if payment is past due
                          const chargeDate = new Date(item.chargeDate)
                          chargeDate.setHours(0, 0, 0, 0)
                          const isPastDue = chargeDate < today

                          return (
                            <div
                              key={item.folioId || index}
                              className={clsx(
                                'border-2 rounded-xl flex flex-row items-center gap-2 p-4 max-w-xl',
                                {
                                  'border-red-400': isPastDue,
                                  'border-cabana': isNextPayment && !isPastDue,
                                  'border-gray-300':
                                    !isNextPayment && !isPastDue
                                }
                              )}
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-md">
                                    {paymentInfo.dueDateLabel}
                                  </span>
                                  {isPastDue && (
                                    <span
                                      className={clsx(
                                        'text-xs px-2 py-0.5 rounded-full',
                                        'bg-red-100 text-red-700 font-medium'
                                      )}
                                    >
                                      Payment needs attention
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm mt-1">
                                  {paymentInfo.description}
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  Billing period: {paymentInfo.billingPeriod}
                                </div>
                              </div>
                              <div
                                className={clsx(
                                  'flex-none text-right min-w-[100px]',
                                  {
                                    'font-bold': isNextPayment && !isPastDue,
                                    'font-medium': !isNextPayment || isPastDue
                                  }
                                )}
                              >
                                ${amountPerInstallment.toFixed(2)}
                              </div>
                            </div>
                          )
                        }
                      )
                    })()}
                  </div>
                  {showBottomGradient && schedule.length > 3 && (
                    <div
                      className="absolute bottom-0 left-0 right-0 h-10
                        bg-gradient-to-b from-white/0 to-white
                        pointer-events-none z-[1]"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Payment History Section */}
            {sortedPaymentHistory.length > 0 && (
              <div className="flex flex-col">
                <h3 className="text-lg font-bold mb-4">Payment History</h3>
                <div className="relative">
                  {showHistoryTopGradient && sortedPaymentHistory.length > 3 && (
                    <div
                      className="absolute top-0 left-0 right-0 h-10
                        bg-gradient-to-t from-white/0 to-white
                        pointer-events-none z-[1]"
                    />
                  )}
                  <div
                    ref={historyScrollContainerRef}
                    className="flex flex-col gap-4 hide-scrollbar pr-2 pb-2"
                    style={{
                      maxHeight:
                        sortedPaymentHistory.length > 3 ? '400px' : 'auto',
                      overflowY:
                        sortedPaymentHistory.length > 3 ? 'auto' : 'visible',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none'
                    }}
                  >
                    {sortedPaymentHistory.map(
                      (item: PaymentHistoryItem, index: number) => {
                        const fallbackChargeDate =
                          chargeDateByFolioId[item.folioId]
                        const paymentInfo = formatPaymentHistoryInfo(
                          item,
                          fallbackChargeDate
                        )
                        const paymentTotal = calculatePaymentTotal(
                          item.payments
                        )

                        return (
                          <div
                            key={`payment-${item.folioId || index}`}
                            className={clsx(
                              'border-2 border-gray-300 rounded-xl',
                              'flex flex-row items-center gap-2',
                              'p-4 max-w-xl'
                            )}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-md">
                                  {paymentInfo.paidDateLabel}
                                </span>
                                <span
                                  className={clsx(
                                    'text-xs px-2 py-0.5 rounded-full',
                                    'bg-green-100 text-green-700 font-medium'
                                  )}
                                >
                                  {paymentInfo.paymentStatus}
                                </span>
                              </div>
                              {paymentInfo.billingPeriod && (
                                <div className="text-sm text-gray-600 mt-1">
                                  Billing period: {paymentInfo.billingPeriod}
                                </div>
                              )}
                            </div>
                            <div className="flex-none text-right min-w-[100px] font-medium">
                              ${paymentTotal.toFixed(2)}
                            </div>
                          </div>
                        )
                      }
                    )}
                  </div>
                  {showHistoryBottomGradient &&
                    sortedPaymentHistory.length > 3 && (
                      <div
                        className="absolute bottom-0 left-0 right-0 h-10
                        bg-gradient-to-b from-white/0 to-white
                        pointer-events-none z-[1]"
                      />
                    )}
                </div>
              </div>
            )}

            {/* Total Amount */}
            <div className="mt-2 pt-4 border-t border-gray-300 max-w-xl">
              <div className="flex justify-between items-center">
                <div className="text-lg font-semibold">Reservation total</div>
                <div className="text-lg font-semibold text-right min-w-[100px]">
                  {isLoading ? (
                    <LoadingSpinner size={LoadingSpinnerSize.Small} />
                  ) : (
                    <>
                      $
                      {(() => {
                        try {
                          // Sum up amounts from payment schedule
                          const scheduleTotal = schedule.reduce(
                            (sum: number, item: PaymentScheduleItem) => {
                              return (
                                sum + Math.abs(item.folioBalance?.amount || 0)
                              )
                            },
                            0
                          )
                          return scheduleTotal.toFixed(2)
                        } catch (e) {
                          console.error('Error calculating total:', e)
                          return '0.00'
                        }
                      })()}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Accordion.Content>
    </section>
  )
}

export default PaymentInstallmentScheduleSection
